import React from 'react'
import { useFormContext, useFieldArray, Controller } from 'react-hook-form'
import { ReferenceInput, SelectInput, useGetList, RaRecord } from 'react-admin'
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

interface TagsInputProps {
  source: string
  filter?: (choice: RaRecord) => boolean
}

interface TagSelectorProps {
  tagCategoryId: number
  source: string
  filter?: (choice: RaRecord) => boolean
}

interface CategorySelectorProps {
  source: string
  filter?: (choice: RaRecord) => boolean
  currentIndex?: number
}

function CategorySelector({
  source,
  filter,
  currentIndex,
}: CategorySelectorProps): JSX.Element {
  const { data = [], isLoading } = useGetList('tag_categories', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 10000 },
  })

  // Get form context to access all form values
  const { getValues } = useFormContext()

  // Get the base source name (e.g., "tags" from "tags[0].tag_category.id")
  const baseSource = source.split('[')[0]

  // Get all current form values
  const formValues = getValues()
  const allItems = formValues[baseSource] || []

  // Filter out categories that are already selected in other rows
  const filtered = data.filter((category) => {
    // Skip filtering for categories without an id
    if (!category.id) return true

    // Check if this category is already selected in another row
    const isAlreadySelected = allItems.some((item, idx) => {
      // Skip the current row
      if (idx === currentIndex) return false

      // Check if the category is already selected in another row
      return item?.tag_category?.id === category.id
    })

    // Filter out already selected categories
    if (isAlreadySelected) return false

    // Apply additional filter if provided
    if (filter) {
      // Create a tag-like object that the filter function expects
      const tagLikeObject = {
        id: category.id,
        tag_category: { name: category.name },
      }
      return filter(tagLikeObject)
    }

    return true
  })

  if (isLoading) return <CircularProgress size={20} />

  return (
    <SelectInput
      source={source}
      label="Tag category"
      choices={filtered}
      optionText="name"
      optionValue="id"
      fullWidth
      sx={{ width: '100%' }}
    />
  )
}

function TagSelector({
  tagCategoryId,
  source,
  filter,
}: TagSelectorProps): JSX.Element {
  // Always call useGetList, but only use the results if tagCategoryId is valid
  const { data = [], isLoading } = useGetList('tags', {
    filter: tagCategoryId
      ? { tag_category_id: tagCategoryId }
      : { tag_category_id: -1 }, // Use -1 as a non-existent ID when no category is selected
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 10000 },
  })

  const filtered = filter ? data.filter(filter) : data

  if (isLoading) return <CircularProgress size={20} />

  return (
    <SelectInput
      source={source}
      label="Tag name"
      choices={filtered}
      optionText="name"
      optionValue="id"
      fullWidth
    />
  )
}

const VendorTagsInput = ({ source, filter }: TagsInputProps) => {
  const { control } = useFormContext()
  const { fields, remove, append } = useFieldArray({ control, name: source })

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
      <Typography variant="h6" gutterBottom>
        Tags
      </Typography>

      {fields.map((field, index) => {
        const categorySource = `${source}[${index}].tag_category.id`
        const tagSource = `${source}[${index}].id`
        const fullItem = control._formValues?.[source]?.[index]

        const tagCategoryId = fullItem?.tag_category?.id
        const tagCategoryName = fullItem?.tag_category?.name

        const shouldHide =
          filter &&
          tagCategoryName &&
          !filter({
            id: tagCategoryId,
            tag_category: { name: tagCategoryName },
          })

        if (shouldHide) return null

        return (
          <Grid
            container
            key={field.id}
            spacing={2}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item xs={5}>
              <CategorySelector
                source={categorySource}
                filter={filter}
                currentIndex={index}
              />
            </Grid>

            <Grid item xs={5}>
              <TagSelector
                tagCategoryId={tagCategoryId}
                source={tagSource}
                filter={filter}
              />
            </Grid>

            <Grid item xs={2}>
              <IconButton onClick={() => remove(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        )
      })}

      <Box mt={2}>
        <button
          type="button"
          onClick={() =>
            append({ tag_category: { id: null, name: '' }, id: null })
          }
        >
          Add Tag
        </button>
      </Box>
    </Box>
  )
}

export default VendorTagsInput
